import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { debounce, isEqual } from "lodash"

import { useBuilderStore } from "@/components/builder/utils/store"

const SAVE_TIMEOUT = 5000
const VERSIONING_TIMEOUT = 30000
const STORAGE_KEY = 'cms:post'

const normalize = craft => JSON.parse(JSON.stringify(craft))

export default function useAutoSave() {
  const { autoSave, disableAutoSave, postBuilder: { item } } = useBuilderStore()
  const { version } = item?.meta || {}

  const [saved, setSaved] = useState(true)
  const [autoSaved, setAutoSaved] = useState(false)
  const lastSavedRef = useRef()
  const lastSeenRef = useRef()
  const pendingRef = useRef()

  useEffect(() => {
    setAutoSaved(false)
  }, [version])

  const cleanLocalStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  const setLocalStorage = useCallback(data => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }, [])

  const getLocalStorage = useCallback(() => {
    const data = localStorage.getItem(STORAGE_KEY)
    if (!data) {
      return null
    }
    return JSON.parse(data)
  }, [])

  const saveDebounced = useMemo(() => debounce(async data => {
    try {
      const result = await autoSave(data)
      if (!result) return
      lastSavedRef.current = normalize(data.craft)
      setSaved(true)
      setAutoSaved(true)
      cleanLocalStorage()
    } catch (error) {
      return;
    }
  }, SAVE_TIMEOUT), [autoSave, cleanLocalStorage])

  const versioningDebounced = useMemo(() => debounce(async data => {
    try {
      await autoSave({ ...data, shouldAudit: true })
    } catch (error) {
      return;
    }
  }, VERSIONING_TIMEOUT), [autoSave])

  const handleAutoSave = useCallback(async data => {
    if (data.craft === lastSeenRef.current) return
    lastSeenRef.current = data.craft

    const craft = normalize(data.craft)

    if (lastSavedRef.current === undefined) {
      lastSavedRef.current = craft
      return
    }

    if (isEqual(craft, lastSavedRef.current)) return

    pendingRef.current = craft
    setSaved(false)
    if (disableAutoSave) return
    setLocalStorage(data)
    saveDebounced(data)
    versioningDebounced(data)
  }, [saveDebounced, versioningDebounced, setLocalStorage, disableAutoSave])

  const markSaved = useCallback(() => {
    saveDebounced.cancel()
    lastSavedRef.current = pendingRef.current ?? lastSavedRef.current
    setSaved(true)
    cleanLocalStorage()
  }, [saveDebounced, cleanLocalStorage])

  const setBaseline = useCallback(craft => {
    saveDebounced.cancel()
    versioningDebounced.cancel()
    lastSavedRef.current = normalize(craft)
    pendingRef.current = undefined
    setSaved(true)
  }, [saveDebounced, versioningDebounced])

  useEffect(() => () => {
    saveDebounced.cancel()
    versioningDebounced.cancel()
  }, [saveDebounced, versioningDebounced])

  return useMemo(() => ({
    handleAutoSave,
    markSaved,
    setBaseline,
    saved,
    autoSaved,
    restoreUnsaved: () => getLocalStorage(),
  }), [handleAutoSave, markSaved, setBaseline, getLocalStorage, saved, autoSaved])
}

